import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import {
    X, Save, Image as ImageIcon,
    CheckCircle, AlertCircle,
    Trash2, Plus, Calendar,
    Globe, GlobeLock
} from 'lucide-react';

function OutsourceAdvertising({ user }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [adData, setAdData] = useState(null);

    // 폼 데이터
    const [formData, setFormData] = useState({
        introduction: '',
        categories: [],
        experience: '',
        equipment: '',
        certifications: '',
        services: '',
        workingHours: '',
        contactInfo: ''
    });

    // 이미지 관련
    const [profileImage, setProfileImage] = useState(null);
    const [profilePreview, setProfilePreview] = useState(null);
    const [portfolioImages, setPortfolioImages] = useState([]);
    const [portfolioPreviews, setPortfolioPreviews] = useState([]);
    const [uploadingProfile, setUploadingProfile] = useState(false);
    const [uploadingPortfolio, setUploadingPortfolio] = useState(false);

    const [isActive, setIsActive] = useState(false);
    const [isPublic, setIsPublic] = useState(true);

    // 외주 카테고리 옵션 (ID 기반 - Register.js / OutsourceOrder.js와 동일)
    const categoryOptions = [
        { id: 'custom_milling', label: 'Custom Milling' },
        { id: '3d_printing', label: '3D Printing' },
        { id: 'design', label: 'Design' },
        { id: 'etc', label: '기타' }
    ];
    const categoryLabel = (id) => categoryOptions.find(c => c.id === id)?.label || id;

    // 메시지
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setErrorMessage('');
    };

    const showError = (message) => {
        setErrorMessage(message);
        setSuccessMessage('');
    };

    // 광고 데이터 로드
    const loadAdvertisement = useCallback(async () => {
        if (!user) return;

        try {
            setLoading(true);
            const adRef = doc(db, 'outsourceAdvertisements', user.uid);
            const adSnap = await getDoc(adRef);

            if (adSnap.exists()) {
                const data = adSnap.data();
                setAdData(data);
                setFormData({
                    introduction: data.introduction || '',
                    categories: data.categories || [],
                    experience: data.experience || '',
                    equipment: data.equipment || '',
                    certifications: data.certifications || '',
                    services: data.services || '',
                    workingHours: data.workingHours || '',
                    contactInfo: data.contactInfo || ''
                });
                setProfilePreview(data.profileImageUrl || null);
                setPortfolioPreviews(data.portfolioImages || []);
                setIsActive(data.isActive || false);
            }

            // 사용자 공개 상태 로드
            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                setIsPublic(userSnap.data().isPublic !== false);
            }
        } catch (error) {
            console.error('광고 데이터 로드 실패:', error);
            showError('데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadAdvertisement();
    }, [loadAdvertisement]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                showError('프로필 이미지는 5MB 이하로 업로드해주세요.');
                return;
            }
            if (!file.type.startsWith('image/')) {
                showError('이미지 파일만 업로드 가능합니다.');
                return;
            }
            // 기존 blob URL 해제
            if (profilePreview && profilePreview.startsWith('blob:')) {
                URL.revokeObjectURL(profilePreview);
            }
            setProfileImage(file);
            setProfilePreview(URL.createObjectURL(file));
        }
    };

    const handlePortfolioImagesChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + portfolioPreviews.length > 10) {
            showError('포트폴리오 이미지는 최대 10개까지 업로드 가능합니다.');
            return;
        }

        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) {
                showError(`${file.name}은(는) 5MB를 초과합니다.`);
                return false;
            }
            return true;
        });

        setPortfolioImages([...portfolioImages, ...validFiles]);
        const newPreviews = validFiles.map(file => URL.createObjectURL(file));
        setPortfolioPreviews([...portfolioPreviews, ...newPreviews]);
    };

    const removePortfolioImage = (index) => {
        // blob URL 해제
        const removedPreview = portfolioPreviews[index];
        if (removedPreview && removedPreview.startsWith('blob:')) {
            URL.revokeObjectURL(removedPreview);
        }
        const newImages = portfolioImages.filter((_, i) => i !== index);
        const newPreviews = portfolioPreviews.filter((_, i) => i !== index);
        setPortfolioImages(newImages);
        setPortfolioPreviews(newPreviews);
    };

    const toggleCategory = (category) => {
        if (formData.categories.includes(category)) {
            setFormData({
                ...formData,
                categories: formData.categories.filter(c => c !== category)
            });
        } else {
            setFormData({
                ...formData,
                categories: [...formData.categories, category]
            });
        }
    };

    // 외주업체 공개/비공개 토글
    const handleTogglePublic = async () => {
        try {
            setSaving(true);
            const newPublicState = !isPublic;

            await updateDoc(doc(db, 'users', user.uid), {
                isPublic: newPublicState,
                updatedAt: serverTimestamp()
            });

            try {
                await updateDoc(doc(db, 'outsourceAdvertisements', user.uid), {
                    isActive: newPublicState,
                    updatedAt: serverTimestamp()
                });
            } catch (e) {
                // 광고가 없는 경우 무시
            }

            setIsPublic(newPublicState);
            showSuccess(newPublicState ? '외주업체가 공개로 전환되었습니다.' : '외주업체가 비공개로 전환되었습니다.');
        } catch (error) {
            showError('설정 변경에 실패했습니다.');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        if (formData.categories.length === 0) {
            showError('최소 1개 이상의 외주 카테고리를 선택해주세요.');
            return;
        }

        if (!formData.introduction.trim()) {
            showError('업체 소개를 입력해주세요.');
            return;
        }

        try {
            setSaving(true);

            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const userData = userDoc.data();

            let profileImageUrl = adData?.profileImageUrl || null;

            // 포트폴리오: 현재 previews에서 기존 URL(http로 시작)만 유지, blob URL은 새 업로드 대상
            let portfolioUrls = portfolioPreviews.filter(url => url && !url.startsWith('blob:'));

            if (profileImage) {
                setUploadingProfile(true);
                const profileRef = ref(storage, `outsourceAds/${user.uid}/profile_${Date.now()}.jpg`);
                await uploadBytes(profileRef, profileImage);
                profileImageUrl = await getDownloadURL(profileRef);
                setUploadingProfile(false);
            }

            if (portfolioImages.length > 0) {
                setUploadingPortfolio(true);
                const baseTime = Date.now();
                const uploadPromises = portfolioImages.map(async (file, index) => {
                    const portfolioRef = ref(storage, `outsourceAds/${user.uid}/portfolio_${baseTime + index}_${index}.jpg`);
                    await uploadBytes(portfolioRef, file);
                    return await getDownloadURL(portfolioRef);
                });
                const newUrls = await Promise.all(uploadPromises);
                portfolioUrls = [...portfolioUrls, ...newUrls];
                setUploadingPortfolio(false);
            }

            const adRef = doc(db, 'outsourceAdvertisements', user.uid);
            const adDataToSave = {
                ...formData,
                profileImageUrl,
                portfolioImages: portfolioUrls,
                isActive: true,
                status: 'active',
                outsourceId: user.uid,
                businessName: userData?.businessName || '',
                email: userData?.email || '',
                phone: userData?.phone || '',
                address: userData?.address || '',
                outsourceCategories: userData?.outsourceCategories || formData.categories,
                updatedAt: serverTimestamp(),
                createdAt: adData?.createdAt || serverTimestamp()
            };

            if (adData) {
                await updateDoc(adRef, adDataToSave);
            } else {
                await setDoc(adRef, adDataToSave);
            }

            setProfileImage(null);
            setPortfolioImages([]);
            setIsActive(true);

            await loadAdvertisement();
            showSuccess('홍보 정보가 저장되었습니다.');
        } catch (error) {
            console.error('저장 실패:', error);
            showError('저장에 실패했습니다.');
        } finally {
            setSaving(false);
            setUploadingProfile(false);
            setUploadingPortfolio(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>외주업체 홍보 관리</h1>
                    <p style={styles.subtitle}>치과/기공소에 우리 외주업체를 홍보하세요</p>
                </div>
                {isActive && (
                    <div style={styles.activeBadge}>
                        <CheckCircle size={16} />
                        <span>홍보 활성</span>
                    </div>
                )}
            </div>

            {/* 메시지 */}
            {successMessage && (
                <div style={styles.successMessage}>
                    <CheckCircle size={20} />
                    {successMessage}
                </div>
            )}
            {errorMessage && (
                <div style={styles.errorMessage}>
                    <AlertCircle size={20} />
                    {errorMessage}
                </div>
            )}

            {/* 외주업체 공개 설정 */}
            <div style={styles.section}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {isPublic ? (
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Globe size={20} color="#fff" />
                            </div>
                        ) : (
                            <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <GlobeLock size={20} color="#fff" />
                            </div>
                        )}
                        <div>
                            <h2 style={{ ...styles.sectionTitle, marginBottom: '4px' }}>
                                {isPublic ? '공개 중' : '비공개'}
                            </h2>
                            <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                                {isPublic ? '외주업체 찾기에 노출됩니다' : '외주업체 찾기에 노출되지 않습니다'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleTogglePublic}
                        disabled={saving}
                        style={{
                            width: '52px',
                            height: '28px',
                            borderRadius: '14px',
                            border: 'none',
                            backgroundColor: isPublic ? '#10b981' : '#94a3b8',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            transition: 'background-color 0.2s',
                            opacity: saving ? 0.6 : 1,
                            flexShrink: 0
                        }}
                    >
                        <div style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            position: 'absolute',
                            top: '3px',
                            left: '3px',
                            transition: 'transform 0.2s',
                            transform: isPublic ? 'translateX(24px)' : 'translateX(0)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                    </button>
                </div>
            </div>

            {/* 미리보기 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>홍보 미리보기</h2>
                <p style={styles.sectionDesc}>다른 업체에서 보게 될 외주업체 프로필입니다</p>

                <div style={styles.bannerPreview}>
                    <div style={styles.bannerHeader}>
                        {profilePreview ? (
                            <img src={profilePreview} alt="프로필" style={styles.bannerProfileImage} />
                        ) : (
                            <div style={styles.bannerProfilePlaceholder}>
                                <ImageIcon size={32} color="#94a3b8" />
                            </div>
                        )}
                        <div style={styles.bannerHeaderInfo}>
                            <h3 style={styles.bannerName}>{user?.businessName || user?.companyName || '외주업체명'}</h3>
                            <p style={styles.bannerAddress}>{user?.address || '주소 미등록'}</p>
                            <div style={styles.bannerContact}>
                                <span>{user?.phone || '연락처 미등록'}</span>
                                <span style={styles.bannerContactDivider}>|</span>
                                <span>{user?.email || '이메일 미등록'}</span>
                            </div>
                        </div>
                    </div>

                    {formData.categories.length > 0 && (
                        <div style={styles.bannerCategories}>
                            {formData.categories.map((cat, idx) => (
                                <span key={idx} style={styles.bannerCategoryTag}>{categoryLabel(cat)}</span>
                            ))}
                        </div>
                    )}

                    {formData.introduction && (
                        <div style={styles.bannerIntroduction}>
                            <p style={styles.bannerIntroText}>
                                {formData.introduction.length > 150
                                    ? formData.introduction.substring(0, 150) + '...'
                                    : formData.introduction}
                            </p>
                        </div>
                    )}

                    {portfolioPreviews.length > 0 && (
                        <div style={styles.bannerPortfolio}>
                            {portfolioPreviews.slice(0, 4).map((img, idx) => (
                                <img key={idx} src={img} alt={`작품${idx+1}`} style={styles.bannerPortfolioImage} />
                            ))}
                            {portfolioPreviews.length > 4 && (
                                <div style={styles.bannerPortfolioMore}>
                                    +{portfolioPreviews.length - 4}
                                </div>
                            )}
                        </div>
                    )}

                    {!formData.introduction && formData.categories.length === 0 && !profilePreview && (
                        <div style={styles.bannerEmpty}>
                            <AlertCircle size={24} color="#94a3b8" />
                            <p>아래 정보를 입력하면 미리보기가 표시됩니다</p>
                        </div>
                    )}
                </div>
            </div>

            {/* 프로필 이미지 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>프로필 이미지</h2>
                <p style={styles.sectionDesc}>외주업체를 대표하는 이미지를 업로드하세요 (최대 5MB)</p>

                <div style={styles.imageUploadContainer}>
                    {profilePreview ? (
                        <div style={styles.profileImagePreview}>
                            <img src={profilePreview} alt="프로필" style={styles.profileImage} />
                            <button
                                onClick={() => {
                                    if (profilePreview && profilePreview.startsWith('blob:')) URL.revokeObjectURL(profilePreview);
                                    setProfileImage(null);
                                    setProfilePreview(null);
                                }}
                                style={styles.removeImageButton}
                                type="button"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    ) : (
                        <label style={styles.uploadButton}>
                            <ImageIcon size={48} />
                            <span>프로필 이미지 업로드</span>
                            <input type="file" accept="image/*" onChange={handleProfileImageChange} style={styles.fileInput} />
                        </label>
                    )}
                </div>
            </div>

            {/* 기본 정보 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>기본 정보</h2>

                <div style={styles.formGroup}>
                    <label style={styles.label}>업체 소개 *</label>
                    <textarea
                        value={formData.introduction}
                        onChange={(e) => setFormData({ ...formData, introduction: e.target.value })}
                        placeholder="외주업체를 간단히 소개해주세요"
                        style={styles.textarea}
                        rows={5}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>외주 카테고리 * (복수 선택 가능)</label>
                    <div style={styles.categoryGrid}>
                        {categoryOptions.map(category => (
                            <button
                                key={category.id}
                                type="button"
                                onClick={() => toggleCategory(category.id)}
                                style={{
                                    ...styles.categoryButton,
                                    ...(formData.categories.includes(category.id) ? styles.categoryButtonActive : {})
                                }}
                            >
                                {category.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>주요 경력</label>
                    <textarea
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        placeholder="주요 경력 사항을 입력해주세요"
                        style={styles.textarea}
                        rows={4}
                    />
                </div>
            </div>

            {/* 상세 정보 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>상세 정보</h2>

                <div style={styles.formGroup}>
                    <label style={styles.label}>보유 장비</label>
                    <textarea
                        value={formData.equipment}
                        onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                        placeholder="보유하고 있는 주요 장비를 입력해주세요"
                        style={styles.textarea}
                        rows={4}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>인증 및 자격</label>
                    <textarea
                        value={formData.certifications}
                        onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                        placeholder="보유하고 있는 인증 및 자격을 입력해주세요"
                        style={styles.textarea}
                        rows={3}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>제공 서비스</label>
                    <textarea
                        value={formData.services}
                        onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                        placeholder="제공하는 주요 서비스를 입력해주세요"
                        style={styles.textarea}
                        rows={4}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>근무 시간</label>
                    <input
                        type="text"
                        value={formData.workingHours}
                        onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })}
                        placeholder="예: 평일 09:00-18:00, 토요일 09:00-13:00"
                        style={styles.input}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label style={styles.label}>연락처 정보</label>
                    <textarea
                        value={formData.contactInfo}
                        onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })}
                        placeholder="전화번호, 이메일 등 연락처 정보를 입력해주세요"
                        style={styles.textarea}
                        rows={3}
                    />
                </div>
            </div>

            {/* 포트폴리오 */}
            <div style={styles.section}>
                <h2 style={styles.sectionTitle}>포트폴리오 이미지</h2>
                <p style={styles.sectionDesc}>작업 사례를 보여주는 이미지를 업로드하세요 (최대 10개, 각 5MB 이하)</p>

                <div style={styles.portfolioGrid}>
                    {portfolioPreviews.map((preview, index) => (
                        <div key={index} style={styles.portfolioItem}>
                            <img src={preview} alt={`포트폴리오 ${index + 1}`} style={styles.portfolioImage} />
                            <button onClick={() => removePortfolioImage(index)} style={styles.removePortfolioButton} type="button">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}

                    {portfolioPreviews.length < 10 && (
                        <label style={styles.addPortfolioButton}>
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                <Plus size={32} />
                                <span>이미지 추가</span>
                            </div>
                            <input type="file" accept="image/*" multiple onChange={handlePortfolioImagesChange} style={styles.fileInput} />
                        </label>
                    )}
                </div>
            </div>

            {/* 저장 */}
            <div style={styles.actions}>
                <button
                    onClick={handleSave}
                    disabled={saving || uploadingProfile || uploadingPortfolio}
                    style={{
                        ...styles.saveButton,
                        opacity: (saving || uploadingProfile || uploadingPortfolio) ? 0.6 : 1,
                        cursor: (saving || uploadingProfile || uploadingPortfolio) ? 'not-allowed' : 'pointer'
                    }}
                >
                    <Save size={20} />
                    {saving ? '저장 중...' : uploadingProfile ? '프로필 업로드 중...' : uploadingPortfolio ? '포트폴리오 업로드 중...' : '저장하기'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '1200px', margin: '0 auto', padding: '32px 20px' },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
    spinner: { width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #8b5cf6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' },
    title: { margin: 0, fontSize: '32px', fontWeight: '700', color: '#0f172a' },
    subtitle: { margin: '8px 0 0 0', fontSize: '16px', color: '#64748b' },
    activeBadge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#ede9fe', color: '#7c3aed', borderRadius: '10px', fontSize: '14px', fontWeight: '600' },
    successMessage: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '12px', marginBottom: '24px', fontSize: '15px', fontWeight: '500' },
    errorMessage: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '12px', marginBottom: '24px', fontSize: '15px', fontWeight: '500' },
    section: { backgroundColor: 'white', borderRadius: '16px', padding: '32px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' },
    sectionTitle: { margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' },
    sectionDesc: { margin: '0 0 24px 0', fontSize: '14px', color: '#64748b' },
    formGroup: { marginBottom: '24px' },
    label: { display: 'block', marginBottom: '8px', fontSize: '15px', fontWeight: '600', color: '#334155' },
    textarea: { width: '100%', padding: '14px 16px', border: '2px solid #e2e8f0', borderRadius: '12px', fontSize: '15px', lineHeight: '1.6', resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
    input: { width: '100%', padding: '14px 16px', border: '2px solid #e2e8f0', borderRadius: '12px', fontSize: '15px', outline: 'none', boxSizing: 'border-box' },
    categoryGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px' },
    categoryButton: { padding: '10px 20px', border: '2px solid #e2e8f0', borderRadius: '10px', backgroundColor: 'white', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s', color: '#475569' },
    categoryButtonActive: { borderColor: '#8b5cf6', backgroundColor: '#f5f3ff', color: '#8b5cf6', fontWeight: '600' },
    imageUploadContainer: { display: 'flex', justifyContent: 'center' },
    profileImagePreview: { position: 'relative', width: '200px', height: '200px', borderRadius: '16px', overflow: 'hidden', border: '3px solid #e2e8f0' },
    profileImage: { width: '100%', height: '100%', objectFit: 'cover' },
    removeImageButton: { position: 'absolute', top: '8px', right: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer' },
    uploadButton: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', width: '200px', height: '200px', border: '3px dashed #e2e8f0', borderRadius: '16px', cursor: 'pointer', color: '#94a3b8', fontSize: '14px', fontWeight: '500', transition: 'all 0.2s' },
    fileInput: { display: 'none' },
    portfolioGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' },
    portfolioItem: { paddingBottom: '100%', position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '2px solid #e2e8f0' },
    portfolioImage: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' },
    removePortfolioButton: { position: 'absolute', top: '8px', right: '8px', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(220,38,38,0.8)', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer' },
    addPortfolioButton: { paddingBottom: '100%', position: 'relative', borderRadius: '12px', border: '3px dashed #e2e8f0', cursor: 'pointer', color: '#94a3b8', fontSize: '13px', fontWeight: '500', overflow: 'hidden' },
    actions: { display: 'flex', justifyContent: 'center', padding: '20px 0' },
    saveButton: { display: 'flex', alignItems: 'center', gap: '10px', padding: '16px 48px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s' },
    bannerPreview: { backgroundColor: '#faf5ff', borderRadius: '16px', padding: '24px', border: '2px solid #ede9fe' },
    bannerHeader: { display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' },
    bannerProfileImage: { width: '80px', height: '80px', borderRadius: '12px', objectFit: 'cover', border: '2px solid #e2e8f0' },
    bannerProfilePlaceholder: { width: '80px', height: '80px', borderRadius: '12px', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    bannerHeaderInfo: { flex: 1 },
    bannerName: { margin: '0 0 4px 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' },
    bannerAddress: { margin: '0 0 4px 0', fontSize: '14px', color: '#64748b' },
    bannerContact: { display: 'flex', gap: '8px', fontSize: '13px', color: '#94a3b8' },
    bannerContactDivider: { color: '#cbd5e1' },
    bannerCategories: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' },
    bannerCategoryTag: { padding: '6px 14px', backgroundColor: '#ede9fe', color: '#7c3aed', borderRadius: '8px', fontSize: '13px', fontWeight: '600' },
    bannerIntroduction: { marginBottom: '12px' },
    bannerIntroText: { margin: 0, fontSize: '14px', color: '#475569', lineHeight: '1.6' },
    bannerPortfolio: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    bannerPortfolioImage: { width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e2e8f0' },
    bannerPortfolioMore: { width: '80px', height: '80px', borderRadius: '8px', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '700', color: '#64748b' },
    bannerEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '32px', color: '#94a3b8', fontSize: '14px' },
};

export default OutsourceAdvertising;
